import React from 'react';
import { X } from 'lucide-react';
import { canonicalInternalHref } from '../lib/routes';

interface MobileMenuProps {
    isOpen: boolean;
    onClose: () => void;
    onNavigate: (path: string) => void;
}

interface NavLink {
    label: string;
    path: string;
    isHighlighted?: boolean;
}

const navLinks: NavLink[] = [
    { label: 'Airports', path: '/airports' },
    { label: 'Lagos Airport Hub', path: '/lagos-airport' },
    { label: 'Abuja Airport Hub', path: '/abuja-airport' },
    { label: 'Domestic Flights', path: '/domestic-flights-nigeria' },
    { label: 'Visa & Immigration', path: '/nigeria-visa-immigration' },
    { label: 'Airport Codes', path: '/airport-codes' },
    { label: 'Airport Services', path: '/airport-services' },
    { label: 'Travel Blog', path: '/blog' },
    { label: 'Passenger Rights', path: '/passenger-rights', isHighlighted: true },
    { label: 'Flight Time Calculator', path: '/flight-calculator' },
    { label: 'Currency Converter', path: '/currency-converter' },
    { label: 'Travel Checklist', path: '/checklist' },
    { label: 'Airport Quiz', path: '/quiz' },
    { label: 'Runway Boss Game', path: '/game' },
    { label: 'All Tools', path: '/tools' },
];

const secondaryLinks: NavLink[] = [
    { label: 'About', path: '/about' },
    { label: 'Contact', path: '/contact' },
    { label: 'Privacy Policy', path: '/privacy' },
];

export function MobileMenu({ isOpen, onClose, onNavigate }: MobileMenuProps) {
    const panelRef = React.useRef<HTMLElement>(null);
    const closeRef = React.useRef(onClose);
    closeRef.current = onClose;
    React.useEffect(() => {
        if (!isOpen) return;
        const opener = document.activeElement as HTMLElement | null;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        panelRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
        const handleKeys = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                closeRef.current();
            }
            if (event.key !== 'Tab') return;
            const controls = panelRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])');
            if (!controls?.length) return;
            const first = controls[0];
            const last = controls[controls.length - 1];
            if (event.shiftKey && (document.activeElement === first || !panelRef.current?.contains(document.activeElement))) {
                event.preventDefault(); last.focus();
            } else if (!event.shiftKey && (document.activeElement === last || !panelRef.current?.contains(document.activeElement))) {
                event.preventDefault(); first.focus();
            }
        };
        document.addEventListener('keydown', handleKeys);
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleKeys);
            if (opener?.isConnected) opener.focus();
        };
    }, [isOpen]);

    const handleNavClick = (event: React.MouseEvent<HTMLAnchorElement>, path: string) => {
        event.preventDefault();
        onNavigate(path);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <>
            <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 mobile-menu-overlay"
                onClick={onClose}
                aria-hidden="true"
            />

            <nav
                ref={panelRef}
                id="mobile-navigation"
                className="fixed top-0 right-0 bottom-0 w-[85%] max-w-sm bg-green-700 text-white z-50 shadow-2xl flex flex-col mobile-menu-panel"
                role="dialog"
                aria-modal="true"
                aria-label="Mobile navigation menu"
            >
                <div className="flex items-center justify-between p-4 border-b border-green-600">
                    <span className="text-xl font-bold">Menu</span>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-green-600 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                        aria-label="Close menu"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto py-4">
                    <div className="space-y-1 px-2">
                        {navLinks.map(link => (
                            <a
                                key={link.path}
                                href={canonicalInternalHref(link.path)}
                                onClick={event => handleNavClick(event, link.path)}
                                className={`w-full text-left px-4 py-3 rounded-lg transition-colors min-h-[44px] flex items-center ${link.isHighlighted ? 'bg-green-600 font-semibold' : 'hover:bg-green-600/50'}`}
                            >
                                {link.label}
                            </a>
                        ))}
                    </div>

                    <div className="my-4 mx-4 border-t border-green-600" />

                    <div className="space-y-1 px-2">
                        {secondaryLinks.map(link => (
                            <a
                                key={link.path}
                                href={canonicalInternalHref(link.path)}
                                onClick={event => handleNavClick(event, link.path)}
                                className="w-full text-left px-4 py-3 rounded-lg hover:bg-green-600/50 transition-colors min-h-[44px] flex items-center text-green-200"
                            >
                                {link.label}
                            </a>
                        ))}
                    </div>
                </div>

                <div className="p-4 border-t border-green-600 text-center text-sm text-green-200">
                    © {new Date().getFullYear()} airports.ng
                </div>
            </nav>
        </>
    );
}
